import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DemonstrationAgenda, DirectusService} from '@frontend/directus';
import {DemonstrationAgendaItemResponse, DemonstrationUseCaseResponse} from '@frontend/shared-api';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {finalize} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {BadgeModule} from 'primeng/badge';
import {ProviderDemonstrationService} from '../services/provider-demonstration.service';
import {SummaryAgendaItem, SummaryUseCase} from './tender-demonstration-agenda.types';
import {LoadingComponent} from '../../../../../shared/components/loading/loading.component';
import {buildUseCaseTranslations} from '../../../../../shared/utils/use-case-translations';
import {TranslocoDirective} from '@jsverse/transloco';

@Component({
  selector: 'app-tender-demonstration-agenda',
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    TranslateDirectusPipe,
    BadgeModule,
    LoadingComponent,
    TranslocoDirective,
  ],
  templateUrl: './tender-demonstration-agenda.component.html',
  styleUrl: './tender-demonstration-agenda.component.scss',
})
export class TenderDemonstrationAgendaComponent implements OnInit {
  private readonly directusService = inject(DirectusService);
  private readonly providerDemonstrationService = inject(ProviderDemonstrationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastService);

  readonly tenderId = input.required<number>();

  protected readonly loading = signal(true);
  protected readonly directusAgendaItems = signal<DemonstrationAgenda[]>([]);
  protected readonly savedAgenda = signal<DemonstrationAgendaItemResponse[]>([]);
  protected readonly savedUseCases = signal<DemonstrationUseCaseResponse[]>([]);
  protected readonly summaryItems = signal<SummaryAgendaItem[]>([]);

  // Track collapsed items. If an item ID is in this set, it is collapsed.
  // Initially empty means all are expanded.
  protected readonly collapsedItems = signal<Set<number>>(new Set());

  // Track expanded use case IDs for descriptions
  protected readonly expandedUseCaseIds = signal<Set<number>>(new Set());

  protected readonly totalDuration = computed(() => {
    return this.summaryItems().reduce((sum, item) => sum + item.duration, 0);
  });

  constructor() {
    effect(() => {
      const directusItems = this.directusAgendaItems();
      const savedAgendaItems = this.savedAgenda();
      const savedUseCaseItems = this.savedUseCases();

      if (directusItems.length === 0 || savedAgendaItems.length === 0) return;

      this.buildSummary(directusItems, savedAgendaItems, savedUseCaseItems);
    });
  }

  ngOnInit() {
    this.loadData();
  }

  protected toggleCollapse(directusId: number): void {
    this.collapsedItems.update((current) => {
      const next = new Set(current);
      if (next.has(directusId)) {
        next.delete(directusId);
      } else {
        next.add(directusId);
      }
      return next;
    });
  }

  protected isCollapsed(directusId: number): boolean {
    return this.collapsedItems().has(directusId);
  }

  protected toggleUseCaseDescription(generatedUseCaseId: number, event: Event): void {
    event.stopPropagation();
    this.expandedUseCaseIds.update((current) => {
      const next = new Set(current);
      if (next.has(generatedUseCaseId)) {
        next.delete(generatedUseCaseId);
      } else {
        next.add(generatedUseCaseId);
      }
      return next;
    });
  }

  protected isUseCaseExpanded(generatedUseCaseId: number): boolean {
    return this.expandedUseCaseIds().has(generatedUseCaseId);
  }

  protected formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')} h`;
    }
    return `${minutes} min`;
  }

  private loadData() {
    this.loading.set(true);

    this.directusService
      .loadDemonstrationAgenda()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (directusData) => {
          this.directusAgendaItems.set(directusData);
          this.loadSavedAgenda();
        },
        error: (error) => {
          console.error('Error loading agenda:', error);
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }

  private loadSavedAgenda() {
    this.providerDemonstrationService
      .getAgenda(this.tenderId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedData) => {
          this.savedAgenda.set(savedData);
          this.loadSavedUsecases();
        },
        error: () => this.savedAgenda.set([]),
      });
  }

  private loadSavedUsecases() {
    this.providerDemonstrationService
      .getUseCases(this.tenderId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (useCases) => this.savedUseCases.set(useCases),
        error: () => this.savedUseCases.set([]),
      });
  }

  private buildSummary(
    directusItems: DemonstrationAgenda[],
    savedAgendaItems: DemonstrationAgendaItemResponse[],
    savedUseCaseItems: DemonstrationUseCaseResponse[],
  ) {
    const sortedAgendaItems = this.sortAgendaItems(savedAgendaItems);

    const summary = sortedAgendaItems
      .map((savedAgenda) => this.buildSummaryItem(directusItems, savedAgenda, savedUseCaseItems))
      .filter((item): item is SummaryAgendaItem => item !== null);

    this.summaryItems.set(summary);
  }

  private sortAgendaItems(
    items: DemonstrationAgendaItemResponse[],
  ): DemonstrationAgendaItemResponse[] {
    return [...items].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
  }

  private buildSummaryItem(
    directusItems: DemonstrationAgenda[],
    savedAgenda: DemonstrationAgendaItemResponse,
    savedUseCaseItems: DemonstrationUseCaseResponse[],
  ): SummaryAgendaItem | null {
    const directusAgenda = directusItems.find(
      (d) => d.id === savedAgenda.demonstrationAgendaDirectusId,
    );
    if (!directusAgenda) return null;

    const includedUseCases = this.getIncludedUseCases(savedAgenda, savedUseCaseItems);

    // If no use cases are included, only show this agenda topic if explicitly enabled in Directus
    // and it has a duration > 0.
    if (includedUseCases.length === 0) {
      if (!this.shouldDisplayWithoutUseCases(directusAgenda, savedAgenda)) {
        return null;
      }

      return {
        directusId: directusAgenda.id,
        translations: directusAgenda.translations,
        orderNumber: savedAgenda.orderNumber ?? 0,
        duration: savedAgenda.duration ?? 0,
        useCases: [],
        abbreviation: directusAgenda.abbreviation || null,
      };
    }

    const actualDuration = includedUseCases.reduce((sum, uc) => sum + uc.duration, 0);

    return {
      directusId: directusAgenda.id,
      translations: directusAgenda.translations,
      orderNumber: savedAgenda.orderNumber ?? 0,
      duration: actualDuration,
      useCases: includedUseCases,
      abbreviation: directusAgenda.abbreviation || null,
    };
  }

  private shouldDisplayWithoutUseCases(
    directusAgenda: DemonstrationAgenda,
    savedAgenda: DemonstrationAgendaItemResponse,
  ): boolean {
    const duration = savedAgenda.duration ?? 0;
    return directusAgenda.display_without_usecases === true && duration > 0;
  }

  private getIncludedUseCases(
    savedAgenda: DemonstrationAgendaItemResponse,
    savedUseCaseItems: DemonstrationUseCaseResponse[],
  ): SummaryUseCase[] {
    const savedIncludedUseCasesForAgenda = savedUseCaseItems.filter(
      (s) => s.demonstrationAgendaItemId === savedAgenda.id && s.included === true,
    );
    if (savedIncludedUseCasesForAgenda.length === 0) return [];

    return savedIncludedUseCasesForAgenda
      .map((savedU) => ({
        generatedUseCaseId: savedU.generatedUseCaseId ?? 0,
        translations: buildUseCaseTranslations(
          savedU.titleDe ?? '',
          savedU.descriptionDe ?? '',
          savedU.titleEn,
          savedU.descriptionEn,
        ),
        duration: savedU.duration ?? 0,
        sortOrder: savedU.sortOrder ?? 0,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }
}
